import {
  createSlice,
  createAsyncThunk,
  PayloadAction
} from '@reduxjs/toolkit'
// @ts-ignore
import { generateCrud } from '@polleverywhere/redux/apis/helpers'

interface IRegistrationAccount {
  id: number,
  total_account_users: number,
  name: string,
  renew_with: string
}

interface IRegistration {
  account: IRegistrationAccount
  created_at: string
  custom_reporting_identifier: string
  id: number
  needs_to_pay_for_participation: boolean
  paid_participation_expires_at?: string
  updated_at: string
}

export interface IParticipantRegistrations {
  loading: boolean
  registrations: Array<IRegistration>
  focussedRegistration?: number
  error?: string
}

export const selectFocussedRegistration = (state: any) => {
  const { registrations, focussedRegistration } = selectParticipantRegistrations(state)
  return registrations.find(reg => reg.id === focussedRegistration)
}

const sliceName = 'participantRegistrations'
const selectors = {}
const thunks = {
  fetch: createAsyncThunk(
    `${sliceName}/fetch`,
    async () => {
      return await generateCrud('api/registrations').read()
    }
  ),
  unregister: createAsyncThunk(
    `${sliceName}/unregister`,
    async (registrationId: number) => {
      return await generateCrud(`api/registrations/${registrationId}`).delete()
    }
  ),
  updateIdentifier: createAsyncThunk(
    `${sliceName}/updateIdentifier`,
    async ({ registrationId, identifier }: { registrationId: number, identifier: string }) => {
      return await generateCrud(`api/registrations/${registrationId}`).update({
        custom_reporting_identifier: identifier
      })
    }
  )
}

const initialState: IParticipantRegistrations = {
  loading: false,
  registrations: []
}

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset () {
      return initialState
    },
    manuallyUpdated (_state, action: PayloadAction<IParticipantRegistrations>) {
      return action.payload
    },
    focusRegistration (state, action: PayloadAction<number>) {
      state.focussedRegistration = action.payload
    },
    clearFocus (state) {
      delete state.focussedRegistration
    }
  },
  extraReducers: (builder) => {
    builder.addCase(thunks.fetch.pending, (state) => {
      state.loading = true
    })
    builder.addCase(thunks.fetch.fulfilled, (state, action) => {
      state.loading = false
      state.registrations = action.payload as Array<IRegistration>
    })
    builder.addCase(thunks.fetch.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message ?? 'Unknown error'
    })

    builder.addCase(thunks.unregister.pending, (state) => {
      state.loading = true
    })
    builder.addCase(thunks.unregister.fulfilled, (state, action) => {
      state.loading = false
      state.registrations = state.registrations.filter(reg => reg.id !== action.meta.arg)
      delete state.focussedRegistration
    })
    builder.addCase(thunks.unregister.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message ?? 'Unknown error'
      delete state.focussedRegistration
    })

    builder.addCase(thunks.updateIdentifier.fulfilled, (state, action) => {
      state.registrations.forEach(reg => {
        if (reg.id === action.meta.arg.registrationId) {
          reg.custom_reporting_identifier = action.meta.arg.identifier
        }
      })
    })
  }
})

const sliceExport = {
  name: sliceName,
  reducer: slice.reducer,
  ...slice.actions,
  ...selectors,
  ...thunks
}

export { sliceExport as slice }

export const selectParticipantRegistrations = (state: any): IParticipantRegistrations => state[sliceName]
